import { differenceInDays, format } from "date-fns";

/**
 * Formats a country's date range with month always visible and the year shown once.
 * 22 - 28 Mar 2027 | 22 Mar - 1 Apr 2027 | 28 Dec 2026 - 3 Jan 2027
 */
export function formatRegionRange(start: Date, end: Date): string {
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();

  if (sameMonth) return `${format(start, "d")} - ${format(end, "d MMM yyyy")}`;
  if (sameYear) return `${format(start, "d MMM")} - ${format(end, "d MMM yyyy")}`;
  return `${format(start, "d MMM yyyy")} - ${format(end, "d MMM yyyy")}`;
}

export interface RegionDateRange {
  region: string;
  startDate: Date;
  endDate: Date;
}

interface ItineraryItemForRegion {
  item_type: string;
  location?: string | null;
  date?: string | null;
  check_in?: string | null;
  check_out?: string | null;
}

// Known city-to-country mappings for common African destinations
const CITY_TO_COUNTRY: Record<string, string> = {
  // South Africa
  'johannesburg': 'South Africa',
  'cape town': 'South Africa',
  'cape toewn': 'South Africa', // Handle typo
  'pretoria': 'South Africa',
  'sun city': 'South Africa',
  'garden route': 'South Africa',
  'george': 'South Africa',
  'oudtshoorn': 'South Africa',
  'durban': 'South Africa',
  'kruger': 'South Africa',
  'kruger national park': 'South Africa',
  'franschhoek': 'South Africa',
  'stellenbosch': 'South Africa',
  'hermanus': 'South Africa',
  'knysna': 'South Africa',
  'port elizabeth': 'South Africa',
  'gqeberha': 'South Africa',
  'bloemfontein': 'South Africa',
  'soweto': 'South Africa',
  'pilanesberg': 'South Africa',
  'madikwe': 'South Africa',
  'sabi sands': 'South Africa',
  
  // Kenya
  'nairobi': 'Kenya',
  'masai mara': 'Kenya',
  'maasai mara': 'Kenya',
  'lake naivasha': 'Kenya',
  'mombasa': 'Kenya',
  'amboseli': 'Kenya',
  'samburu': 'Kenya',
  'lake nakuru': 'Kenya',
  'diani': 'Kenya',
  'lamu': 'Kenya',
  'mount kenya': 'Kenya',
  'tsavo': 'Kenya',
  
  // Tanzania
  'arusha': 'Tanzania',
  'serengeti': 'Tanzania',
  'zanzibar': 'Tanzania',
  'ngorongoro': 'Tanzania',
  'kilimanjaro': 'Tanzania',
  'dar es salaam': 'Tanzania',
  'tarangire': 'Tanzania',
  'lake manyara': 'Tanzania',
  'stone town': 'Tanzania',
  
  // Rwanda
  'kigali': 'Rwanda',
  'volcanoes national park': 'Rwanda',
  'akagera': 'Rwanda',
  'nyungwe': 'Rwanda',
  
  // Uganda
  'kampala': 'Uganda',
  'bwindi': 'Uganda',
  'entebbe': 'Uganda',
  'queen elizabeth': 'Uganda',
  'murchison falls': 'Uganda',
  
  // Botswana
  'kasane': 'Botswana',
  'chobe': 'Botswana',
  'okavango': 'Botswana',
  'okavango delta': 'Botswana',
  'maun': 'Botswana',
  'moremi': 'Botswana',
  'makgadikgadi': 'Botswana',
  'gaborone': 'Botswana',
  
  // Zimbabwe
  'victoria falls': 'Zimbabwe',
  'harare': 'Zimbabwe',
  'hwange': 'Zimbabwe',
  'mana pools': 'Zimbabwe',
  
  // Zambia
  'livingstone': 'Zambia',
  'lusaka': 'Zambia',
  'south luangwa': 'Zambia',
  'lower zambezi': 'Zambia',
  
  // Namibia
  'windhoek': 'Namibia',
  'sossusvlei': 'Namibia',
  'etosha': 'Namibia',
  'swakopmund': 'Namibia',
  'skeleton coast': 'Namibia',
  'fish river canyon': 'Namibia',
  
  // Mozambique
  'maputo': 'Mozambique',
  'bazaruto': 'Mozambique',
  'vilanculos': 'Mozambique',
  'tofo': 'Mozambique',
  
  // Egypt
  'cairo': 'Egypt',
  'luxor': 'Egypt',
  'aswan': 'Egypt',
  'sharm el sheikh': 'Egypt',
  'hurghada': 'Egypt',
  'giza': 'Egypt',
  
  // Morocco
  'marrakech': 'Morocco',
  'casablanca': 'Morocco',
  'fez': 'Morocco',
  'chefchaouen': 'Morocco',
  'essaouira': 'Morocco',
  
  // Mauritius
  'port louis': 'Mauritius',
  
  // Seychelles
  'mahe': 'Seychelles',
  'praslin': 'Seychelles',
  'la digue': 'Seychelles',
  
  // Madagascar
  'antananarivo': 'Madagascar',
};

// List of known countries (used to check if location itself is a country name)
const KNOWN_COUNTRIES = [
  'South Africa',
  'Kenya',
  'Tanzania',
  'Rwanda',
  'Uganda',
  'Botswana',
  'Zimbabwe',
  'Zambia',
  'Namibia',
  'Mozambique',
  'Egypt',
  'Morocco',
  'Mauritius',
  'Seychelles',
  'Madagascar',
  'Ethiopia',
  'Ghana',
  'Senegal',
  'Malawi',
  'Lesotho',
  'Eswatini',
  'Swaziland',
];

/**
 * Resolves a free-text location string to a known country, if possible.
 */
function resolveCountry(rawLocation: string | null | undefined): string | undefined {
  const location = (rawLocation || '').toLowerCase().trim();
  if (!location) return undefined;

  let country: string | undefined = KNOWN_COUNTRIES.find(c => c.toLowerCase() === location);

  if (!country) country = CITY_TO_COUNTRY[location];

  if (!country) country = KNOWN_COUNTRIES.find(c => location.includes(c.toLowerCase()));

  if (!country) {
    for (const [city, countryName] of Object.entries(CITY_TO_COUNTRY)) {
      if (location.includes(city)) {
        country = countryName;
        break;
      }
    }
  }

  return country;
}

export interface RegionFallback {
  /** Countries listed on the lead, in order */
  countries?: string[] | null;
  /** Free-text destination on the lead */
  destination?: string | null;
}

/**
 * Extracts regions (countries) from itinerary items with their date ranges.
 * Items without a usable location fall back to the lead's own countries/destination
 * so that custom entries don't shorten the displayed period.
 */
export function extractRegionsFromItems(
  items: ItineraryItemForRegion[],
  fallback?: RegionFallback
): RegionDateRange[] {
  const regionDates: Map<string, { min: Date; max: Date }> = new Map();

  const fallbackCountry =
    (fallback?.countries || [])
      .map((c) => resolveCountry(c) || (c || '').trim())
      .filter(Boolean)[0] || resolveCountry(fallback?.destination);

  for (const item of items) {
    // Determine the relevant dates for this item
    const relevantDate = item.item_type === 'accommodation' ? item.check_in : item.date;
    const endDate = item.item_type === 'accommodation' ? item.check_out : item.date;

    if (!relevantDate) continue;

    const country = resolveCountry(item.location) || fallbackCountry;
    if (!country) continue;

    // Update region date range
    const itemStart = new Date(relevantDate);
    const itemEnd = endDate ? new Date(endDate) : itemStart;

    const existing = regionDates.get(country);
    if (existing) {
      existing.min = itemStart < existing.min ? itemStart : existing.min;
      existing.max = itemEnd > existing.max ? itemEnd : existing.max;
    } else {
      regionDates.set(country, { min: itemStart, max: itemEnd });
    }
  }

  // Convert to sorted array (by start date)
  return Array.from(regionDates.entries())
    .map(([region, dates]) => ({ region, startDate: dates.min, endDate: dates.max }))
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
}


/**
 * Calculates total nights from start and end dates
 */
export function calculateTotalNights(startDate: string | null | undefined, endDate: string | null | undefined): number {
  if (!startDate || !endDate) return 0;
  const nights = differenceInDays(new Date(endDate), new Date(startDate));
  return nights > 0 ? nights : 0;
}
